import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnChanges, Output, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatDialog } from '@angular/material/dialog';
import { forkJoin } from 'rxjs';
import { DesignService } from '../../../../services/design.service';
import { ChemicalsService } from '../../../../services/chemicals.service';
import { DesignNotesProvider } from '../../../../services/design-notes-provider.service';
import { TestResultsComponent } from '../test-results/test-results.component';
import {
  CommentsDialogComponent,
  CommentsDialogData,
} from '@shared/components/comments-dialog/comments-dialog.component';
import {
  MaterialDetailsPopupComponent,
  MaterialDetailsPopupData,
} from './material-details-popup/material-details-popup.component';
import {
  AddFormulationMaterialDto,
  DesignIterationDto,
  DesignMaterialRowDto,
  DesignTestInputDto,
  FormulationComponentSequenceDto,
  FormulationComponentDto,
  JobRequestIterationsDto,
  SaveDesignDto,
  SetShippingVolumeDto,
  UpdateFormulationComponentSpecificGravityDto,
  UpdateFormulationSequenceDto,
} from '@models/job-request-iterations-dto.interface';
import { ChemicalListItemDto } from '@models/chemical-list-item-dto.interface';
import {
  FormulationBarcodeDialogComponent,
  FormulationBarcodeDialogData,
} from './formulation-barcode-dialog/formulation-barcode-dialog.component';


type IterationActionKind = 'add-iteration' | 'start-from-scratch' | 'create-shipping' | 'ensure-first';

const DENSITY_CHANGE_JOB_REQUEST_TYPE_ID = 2;
const DEFAULT_LAB_VOLUME_ML = 350;
const BARRELS_PER_GALLON = 10.82;

interface IterationActionItem {
  kind: IterationActionKind;
  label: string;
  designId?: number;
}

@Component({
  selector: 'app-formulation-iterations',
  standalone: true,
  imports: [CommonModule, MatButtonModule, MatIconModule, MatTooltipModule, MatProgressSpinnerModule, TestResultsComponent],
  templateUrl: './formulation-iterations.component.html',
  styleUrl: './formulation-iterations.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FormulationIterationsComponent implements OnChanges {
  @Input() data: JobRequestIterationsDto | null = null;
  @Output() changed = new EventEmitter<void>();
  @Output() testSelected = new EventEmitter<{ designId: number; designName: string; isEditable: boolean } | null>();

  private readonly designService = inject(DesignService);
  private readonly chemicalsService = inject(ChemicalsService);
  private readonly designNotesProvider = inject(DesignNotesProvider);
  private readonly dialog = inject(MatDialog);

  protected isSaving = signal(false);
  protected isAddingMaterial = signal(false);
  protected isAddingBlend = signal(false);
  protected isUpdatingSequence = signal(false);
  protected isRemoving = signal(false);
  protected isSettingShippingVolume = signal(false);
  protected isSavingSpecificGravity = signal(false);
  /** Draft strings for the shipping volume cell keyed by designId. */
  protected volumeGalDraft = signal<Record<number, string>>({});
  protected volumeBarrelDraft = signal<Record<number, string>>({});
  protected actionError = signal<string | null>(null);
  protected actionWarning = signal<string | null>(null);
  protected chemicals = signal<ChemicalListItemDto[]>([]);
  protected selectedChemicalId = signal<number | null>(null);
  protected selectedBlendChemicalIds = signal<Record<number, number | null>>({});
  protected chemicalSearchTerm = signal<string>('');
  protected blendChemicalSearchTerms = signal<Record<number, string>>({});
  protected showChemicalSearch = signal<boolean>(false);
  protected showBlendChemicalSearch = signal<Record<number, boolean>>({});
  protected sequenceDraft = signal<Record<number, string>>({});
  protected specificGravityDraft = signal<Record<number, string>>({});
  protected designNameDraft = signal<Record<number, string>>({});
  protected dilutionPercentDraft = signal<Record<number, string>>({});
  protected weightDraft = signal<Record<string, string>>({});
  protected originalWeights = signal<Record<string, number>>({});
  protected commentPresence = signal<Record<number, boolean>>({});
  private readonly designWeightMaps = signal<Map<number, Map<number, number>>>(new Map());
  protected activeTestDesignId = signal<number | null>(null);
  private readonly isEnsuringFirstIteration = signal(false);

  ngOnChanges(): void {
    // Reset saving state when parent reloads data
    this.isSaving.set(false);
    this.isAddingMaterial.set(false);
    this.isAddingBlend.set(false);
    this.isUpdatingSequence.set(false);
    this.isRemoving.set(false);
    this.isSettingShippingVolume.set(false);
    this.isSavingSpecificGravity.set(false);
    this.actionError.set(null);
    this.actionWarning.set(null);
    this.selectedBlendChemicalIds.set({});
    this.dilutionPercentDraft.set({});
    this.initializeSequenceDraft();
    this.initializeSpecificGravityDraft();
    this.initializeDesignNameDraft();
    this.computeWeightMaps();
    this.initializeWeightDraft();
    this.initializeVolumeGalDraft();
    this.loadCommentStates();
    this.loadChemicalsIfNeeded();
    this.updateLegacyCutbackWarnings();
    this.ensureFirstIterationIfNeeded();
  }

  /** Map from formulationComponentId → weight for a given design column */
  protected weightMap(design: DesignIterationDto): Map<number, number> {
    return this.designWeightMaps().get(design.id) ?? new Map();
  }

  protected onAddFirstIteration(): void {
    if (!this.data) return;
    this.addIteration(undefined);
  }

  protected onOpenComments(design: DesignIterationDto): void {
    this.dialog.open<CommentsDialogComponent, CommentsDialogData>(
      CommentsDialogComponent,
      {
        data: {
          designId: design.id,
          designName: design.name,
        },
        width: '700px',
        disableClose: true,
      }
    ).afterClosed().subscribe(() => this.refreshCommentState(design.id));
  }

  protected commentsActionLabel(design: DesignIterationDto): string | null {
    const hasComments = this.commentPresence()[design.id];
    if (hasComments === undefined) return null;
    return hasComments ? 'View Comments' : 'Add Comments';
  }

  private loadCommentStates(): void {
    for (const design of this.data?.designs ?? []) {
      if (this.commentPresence()[design.id] === undefined) {
        this.refreshCommentState(design.id);
      }
    }
  }

  private refreshCommentState(designId: number): void {
    this.designNotesProvider.getNotes(designId).subscribe({
      next: notes => this.commentPresence.update(current => ({ ...current, [designId]: notes.length > 0 })),
    });
  }

  protected onOpenBarcodeDialog(component: FormulationComponentDto): void {
    if (!this.data?.isEditable || this.isAnyActionSaving()) return;

    this.dialog
      .open<FormulationBarcodeDialogComponent, FormulationBarcodeDialogData, boolean>(
        FormulationBarcodeDialogComponent,
        {
          data: {
            jobRequestId: this.data.jobRequestId,
            formulationComponentId: component.id,
            componentName: component.name ?? component.materialName ?? component.chemicalName,
            currentBarcode: component.materialBarcode,
          },
          width: '760px',
          disableClose: true,
        }
      )
      .afterClosed()
      .subscribe(saved => {
        if (saved) {
          this.changed.emit();
        }
      });
  }

  protected onOpenMaterialDetails(component: FormulationComponentDto, event: Event): void {
    event.stopPropagation();
    if (!component.materialId) return;

    this.dialog.open<MaterialDetailsPopupComponent, MaterialDetailsPopupData>(
      MaterialDetailsPopupComponent,
      {
        data: {
          materialId: component.materialId,
          title: component.materialBarcode,
        },
        width: '600px',
      }
    );
  }

  protected onUnassignBarcode(component: FormulationComponentDto, event: Event): void {
    event.stopPropagation();

    if (!this.data?.isEditable || this.isAnyActionSaving()) return;
    if (!component.componentBarcode) return;

    this.actionError.set(null);
    this.isSaving.set(true);

    this.designService
      .unassignFormulationComponentBarcode(this.data.jobRequestId, component.id)
      .subscribe({
        next: () => {
          this.isSaving.set(false);
          this.changed.emit();
        },
        error: () => {
          this.isSaving.set(false);
          this.actionError.set('Unable to unassign barcode.');
        },
      });
  }

  protected onAddNextIteration(lastDesign: DesignIterationDto): void {
    this.addIteration(lastDesign);
  }

  protected iterationActions(): IterationActionItem[] {
    const data = this.data;
    if (!data || !data.isEditable) return [];

    const designs = data.designs ?? [];
    if (designs.length === 0) {
      return [{ kind: 'ensure-first', label: 'Add Iteration' }];
    }

    const actions: IterationActionItem[] = [];

    const lastInBucketDesigns = designs.filter(d => this.isLastIterationInBucket(d.id, designs));
    lastInBucketDesigns.forEach(design => {
      actions.push({
        kind: 'add-iteration',
        designId: design.id,
        label: `New Iteration for ${this.displayDesignName(design)}`,
      });
    });

    actions.push({ kind: 'start-from-scratch', label: 'Start From Scratch' });

    const nonShippingDesigns = designs.filter(d => !this.isShippingDesign(d));
    nonShippingDesigns.forEach(design => {
      actions.push({
        kind: 'create-shipping',
        designId: design.id,
        label: `Shipping Volume for ${this.displayDesignName(design)}`,
      });
    });

    return actions;
  }

  protected onIterationAction(action: IterationActionItem, menu?: HTMLDetailsElement): void {
    menu?.removeAttribute('open');

    if (!this.data || this.isAnyActionSaving()) return;

    this.actionError.set(null);

    if (action.kind === 'create-shipping' && this.hasPendingWeightChanges()) {
      if (!action.designId) {
        this.actionError.set('Unable to create shipping iteration.');
        return;
      }

      const designId = action.designId;
      this.onSaveWeightChanges(() => this.createShippingIteration(designId));
      return;
    }

    this.isSaving.set(true);

    switch (action.kind) {
      case 'add-iteration': {
        const baseDesign = action.designId
          ? this.data.designs.find(d => d.id === action.designId)
          : undefined;
        this.designService
          .addIteration(this.data.jobRequestId, { baseDesignId: baseDesign?.id })
          .subscribe({
            next: () => {
              this.isSaving.set(false);
              this.changed.emit();
            },
            error: () => {
              this.isSaving.set(false);
              this.actionError.set('Unable to add iteration.');
            },
          });
        break;
      }
      case 'start-from-scratch': {
        const seedDesignId = this.lastNonShippingDesignId();
        this.designService
          .startFromScratch(this.data.jobRequestId, { seedDesignId: seedDesignId ?? undefined })
          .subscribe({
            next: () => {
              this.isSaving.set(false);
              this.changed.emit();
            },
            error: () => {
              this.isSaving.set(false);
              this.actionError.set('Unable to start iteration from scratch.');
            },
          });
        break;
      }
      case 'create-shipping': {
        if (!action.designId) {
          this.isSaving.set(false);
          this.actionError.set('Unable to create shipping iteration.');
          return;
        }

        this.createShippingIteration(action.designId);
        break;
      }
      case 'ensure-first': {
        this.designService.ensureFirstIteration(this.data.jobRequestId).subscribe({
          next: () => {
            this.isSaving.set(false);
            this.changed.emit();
          },
          error: () => {
            this.isSaving.set(false);
            this.actionError.set('Unable to add first iteration.');
          },
        });
        break;
      }
      default: {
        this.isSaving.set(false);
        break;
      }
    }
  }

  protected onDeleteDesign(design: DesignIterationDto): void {
    if (!this.data) return;
    if (!this.canEditDesign(design)) return;
    if (!confirm(`Delete iteration "${design.name ?? design.id}"?`)) return;

    this.isSaving.set(true);
    this.designService.deleteDesign(design.id).subscribe({
      next: () => { this.isSaving.set(false); this.changed.emit(); },
      error: () => this.isSaving.set(false),
    });
  }

  protected designNameValue(designId: number): string {
    return this.designNameDraft()[designId] ?? '';
  }

  protected onDesignNameInput(designId: number, value: string): void {
    if (!this.isDesignEditableById(designId)) return;

    const next = { ...this.designNameDraft() };
    next[designId] = value;
    this.designNameDraft.set(next);
  }

  protected onDesignNameEnter(event: Event): void {
    event.preventDefault();
    const input = event.target as HTMLInputElement | null;
    input?.blur();
  }

  protected focusNextInput(event: KeyboardEvent): void {
    const table = event.currentTarget as HTMLTableElement;
    const target = event.target;
    if (!(target instanceof HTMLElement)) return;

    const editableControls = Array.from(table.querySelectorAll<HTMLElement>(
      'input:not([type="hidden"]):not([type="button"]):not([type="submit"]):not([type="reset"]):not([readonly]):not([disabled]):not([tabindex="-1"]), textarea:not([readonly]):not([disabled]):not([tabindex="-1"]), select:not([disabled]):not([tabindex="-1"])'
    ));
    const currentIndex = editableControls.indexOf(target);
    if (currentIndex < 0) return;

    event.preventDefault();
    if (currentIndex < editableControls.length - 1) {
      editableControls[currentIndex + 1].focus();
    }
  }

  protected onCommitDesignName(design: DesignIterationDto, index: number): void {
    if (!this.data || !this.canEditDesign(design) || this.isAnyActionSaving()) return;

    const raw = this.designNameValue(design.id);
    const nextName = raw.trim();
    const currentName = this.defaultDesignName(design, index);

    if (!nextName) {
      this.actionError.set('Iteration name cannot be empty.');
      this.onDesignNameInput(design.id, currentName);
      return;
    }

    if (nextName === currentName) {
      if (raw !== nextName) {
        this.onDesignNameInput(design.id, nextName);
      }
      return;
    }

    const dto: SaveDesignDto = {
      name: nextName,
      designTypeId: design.designTypeId,
      previousDesignId: design.previousDesignId ?? undefined,
      labVolume: design.labVolume ?? undefined,
      baseFluidDilutionPercent: design.baseFluidDilutionPercent ?? undefined,
      materials: design.materials.map(m => ({
        id: m.id,
        weight: m.weight,
        formulationComponentId: m.formulationComponentId ?? undefined,
      })),
    };

    this.actionError.set(null);
    this.isSaving.set(true);
    this.designService.updateDesign(design.id, dto).subscribe({
      next: () => {
        this.isSaving.set(false);
        this.onDesignNameInput(design.id, nextName);
        this.changed.emit();
      },
      error: () => {
        this.isSaving.set(false);
        this.actionError.set('Unable to update iteration name.');
        this.onDesignNameInput(design.id, currentName);
      },
    });
  }

  protected onChemicalSelectionChange(value: string): void {
    const parsed = Number.parseInt(value, 10);
    this.selectedChemicalId.set(Number.isNaN(parsed) ? null : parsed);
  }

  protected onAddMaterial(parentFormulationComponentId?: number): void {
    if (!this.data || !this.data.isEditable || this.isAnyActionSaving()) return;

    const chemicalId = parentFormulationComponentId
      ? this.blendSelectedChemicalId(parentFormulationComponentId)
      : this.selectedChemicalId();

    if (!chemicalId || chemicalId <= 0) {
      this.actionError.set('Select a chemical before adding material.');
      return;
    }

    const dto: AddFormulationMaterialDto = {
      chemicalId,
      parentFormulationComponentId,
    };

    this.actionError.set(null);
    this.isAddingMaterial.set(true);
    this.designService.addFormulationMaterial(this.data.jobRequestId, dto).subscribe({
      next: () => {
        this.isAddingMaterial.set(false);
        if (parentFormulationComponentId) {
          const next = { ...this.selectedBlendChemicalIds() };
          next[parentFormulationComponentId] = null;
          this.selectedBlendChemicalIds.set(next);
        } else {
          this.selectedChemicalId.set(null);
        }
        this.changed.emit();
      },
      error: () => {
        this.isAddingMaterial.set(false);
        this.actionError.set('Unable to add material.');
      },
    });
  }

  protected onAddBlend(): void {
    if (!this.data || !this.data.isEditable || this.isAnyActionSaving()) return;

    this.actionError.set(null);
    this.isAddingBlend.set(true);
    this.designService.addBlend(this.data.jobRequestId, {}).subscribe({
      next: () => {
        this.isAddingBlend.set(false);
        this.changed.emit();
      },
      error: () => {
        this.isAddingBlend.set(false);
        this.actionError.set('Unable to add blend.');
      },
    });
  }

  protected onRemoveComponent(fc: FormulationComponentDto): void {
    if (!this.data || !this.data.isEditable || this.isAnyActionSaving()) return;
    const name = fc.name ?? fc.materialName ?? fc.chemicalName ?? `Component ${fc.id}`;
    if (!confirm(`Remove "${name}" from the formulation?`)) return;

    this.actionError.set(null);
    this.isRemoving.set(true);
    this.designService.removeFormulationComponent(this.data.jobRequestId, fc.id).subscribe({
      next: () => { this.isRemoving.set(false); this.changed.emit(); },
      error: () => { this.isRemoving.set(false); this.actionError.set('Unable to remove component.'); },
    });
  }

  protected onRemoveBlend(fc: FormulationComponentDto): void {
    if (!this.data || !this.data.isEditable || this.isAnyActionSaving()) return;
    const name = fc.name ?? 'Dilution Fluid';
    if (!confirm(`Remove the "${name}" blend and all its materials?`)) return;

    this.actionError.set(null);
    this.isRemoving.set(true);
    this.designService.removeFormulationComponent(this.data.jobRequestId, fc.id).subscribe({
      next: () => { this.isRemoving.set(false); this.changed.emit(); },
      error: () => { this.isRemoving.set(false); this.actionError.set('Unable to remove blend.'); },
    });
  }

  protected onSequenceInput(formulationComponentId: number, value: string): void {
    const next = { ...this.sequenceDraft() };
    next[formulationComponentId] = value;
    this.sequenceDraft.set(next);
  }

  protected sequenceValue(formulationComponentId: number): string {
    return this.sequenceDraft()[formulationComponentId] ?? '';
  }

  protected onSpecificGravityInput(formulationComponentId: number, value: string): void {
    const component = this.data?.formulationComponents.find(fc => fc.id === formulationComponentId);
    if (component && this.isSpecificGravityReadOnly(component)) {
      return;
    }

    const next = { ...this.specificGravityDraft() };
    next[formulationComponentId] = value;
    this.specificGravityDraft.set(next);
  }

  protected specificGravityValue(formulationComponentId: number): string {
    return this.specificGravityDraft()[formulationComponentId] ?? '';
  }

  protected onCommitSpecificGravity(component: FormulationComponentDto): void {
    if (!this.data || !this.data.isEditable || this.isAnyActionSaving()) return;
    if (this.isSpecificGravityReadOnly(component)) {
      this.onSpecificGravityInput(component.id, String(component.specificGravity));
      return;
    }

    const parsed = this.parseSpecificGravity(this.specificGravityValue(component.id));
    if (parsed === null || parsed === component.specificGravity) {
      // Revert to the last known-good value when invalid or unchanged.
      this.onSpecificGravityInput(component.id, String(component.specificGravity));
      return;
    }

    const dto: UpdateFormulationComponentSpecificGravityDto = { specificGravity: parsed };

    this.actionError.set(null);
    this.isSavingSpecificGravity.set(true);
    this.designService.updateFormulationComponentSpecificGravity(this.data.jobRequestId, component.id, dto).subscribe({
      next: () => {
        this.isSavingSpecificGravity.set(false);
        this.changed.emit();
      },
      error: () => {
        this.isSavingSpecificGravity.set(false);
        this.onSpecificGravityInput(component.id, String(component.specificGravity));
        this.actionError.set('Unable to update specific gravity.');
      },
    });
  }

  private parseSpecificGravity(value: string): number | null {
    const trimmed = value.trim();
    if (!trimmed) return null;

    const parsed = Number.parseFloat(trimmed);
    if (Number.isNaN(parsed) || parsed <= 0) {
      return null;
    }

    return parsed;
  }

  // Job request types that support sequence reordering (matches JobRequestTypeIds in the API)
  private static readonly SEQUENCE_ALLOWED_TYPES = new Set([2, 3]); // DensityChange = 2, PilotTest = 3

  protected canUpdateSequence(): boolean {
    if (!this.data || !this.data.isEditable || this.data.formulationComponents.length === 0 || this.isAnyActionSaving()) {
      return false;
    }

    if (!FormulationIterationsComponent.SEQUENCE_ALLOWED_TYPES.has(this.data.jobRequestTypeId)) {
      return false;
    }

    return this.data.formulationComponents.some(fc => {
      const original = fc.sequenceNumber;
      const draft = this.parseSequenceNumber(this.sequenceValue(fc.id));
      return original !== draft;
    });
  }

  protected onUpdateSequence(): void {
    if (!this.data || !this.canUpdateSequence()) return;

    const items: FormulationComponentSequenceDto[] = this.data.formulationComponents.map(fc => ({
      formulationComponentId: fc.id,
      sequenceNumber: this.parseSequenceNumber(this.sequenceValue(fc.id)),
    }));

    const dto: UpdateFormulationSequenceDto = { items };

    this.actionError.set(null);
    this.isUpdatingSequence.set(true);
    this.designService.updateFormulationSequence(this.data.jobRequestId, dto).subscribe({
      next: () => {
        this.isUpdatingSequence.set(false);
        this.changed.emit();
      },
      error: () => {
        this.isUpdatingSequence.set(false);
        this.actionError.set('Unable to update sequence.');
      },
    });
  }

  private addIteration(previousDesign: DesignIterationDto | undefined): void {
    if (!this.data) return;

    this.isSaving.set(true);
    this.designService.addIteration(this.data.jobRequestId, { baseDesignId: previousDesign?.id }).subscribe({
      next: () => { this.isSaving.set(false); this.changed.emit(); },
      error: () => this.isSaving.set(false),
    });
  }

  private ensureFirstIterationIfNeeded(): void {
    const data = this.data;
    if (!data || !data.isEditable || data.designs.length !== 0 || this.isEnsuringFirstIteration()) return;

    this.isEnsuringFirstIteration.set(true);
    this.isSaving.set(true);
    this.designService.ensureFirstIteration(data.jobRequestId).subscribe({
      next: () => {
        this.isEnsuringFirstIteration.set(false);
        this.isSaving.set(false);
        this.changed.emit();
      },
      error: () => {
        this.isEnsuringFirstIteration.set(false);
        this.isSaving.set(false);
      },
    });
  }

  protected canEditDesign(design: DesignIterationDto): boolean {
    return !!this.data?.isEditable && design.isEditable;
  }

  private isDesignEditableById(designId: number): boolean {
    const design = this.data?.designs.find(d => d.id === designId);
    return !!design && this.canEditDesign(design);
  }

  private isLastIterationInBucket(designId: number, designs: DesignIterationDto[]): boolean {
    return !designs.some(d => d.previousDesignId === designId);
  }

  private isShippingDesign(design: DesignIterationDto): boolean {
    return (design.designTypeName ?? '').trim().toLowerCase() === 'shipping';
  }

  private lastNonShippingDesignId(): number | null {
    const designs = this.data?.designs ?? [];
    const nonShippingDesigns = designs.filter(design => !this.isShippingDesign(design));
    const candidates = nonShippingDesigns.length > 0 ? nonShippingDesigns : designs;
    return candidates.reduce<number | null>(
      (latestId, design) => latestId === null || design.id > latestId ? design.id : latestId,
      null
    );
  }

  private displayDesignName(design: DesignIterationDto): string {
    const explicitName = design.name?.trim();
    if (explicitName) return explicitName;

    const index = this.data?.designs.findIndex(d => d.id === design.id) ?? -1;
    return index >= 0 ? `Iteration ${index + 1}` : `Iteration ${design.id}`;
  }

  protected trackByDesignId(_: number, d: DesignIterationDto): number { return d.id; }
  protected trackByFcId(_: number, fc: FormulationComponentDto): number { return fc.id; }
  protected trackByMaterialId(_: number, m: DesignMaterialRowDto): number { return m.id; }

  protected onToggleTests(design: DesignIterationDto): void {
    if (this.activeTestDesignId() === design.id) {
      this.activeTestDesignId.set(null);
      this.testSelected.emit(null);
      return;
    }
    this.activeTestDesignId.set(design.id);
    this.testSelected.emit({
      designId: design.id,
      designName: design.name ?? `Iteration ${(this.data?.designs.findIndex(d => d.id === design.id) ?? 0) + 1}`,
      isEditable: this.canEditDesign(design),
    });
  }

  protected testGroupSummary(design: DesignIterationDto): string {
    const total = design.testGroups.reduce((sum, tg) => sum + tg.testInputs.length, 0);
    return total === 0 ? 'Tests (0)' : `Tests (${total})`;
  }



  protected hasBlend(): boolean {
    if (!this.data) return false;
    return this.data.formulationComponents.some(fc => this.isBlendComponent(fc));
  }

  protected showMixingTimeColumn(): boolean {
    return this.data?.jobRequestTypeId !== DENSITY_CHANGE_JOB_REQUEST_TYPE_ID;
  }

  protected hasShippingDesign(): boolean {
    return (this.data?.designs ?? []).some(d => d.isShipping);
  }

  protected showLocationColumn(): boolean {
    return this.data?.jobRequestTypeId !== DENSITY_CHANGE_JOB_REQUEST_TYPE_ID;
  }

  protected isBlendComponent(component: FormulationComponentDto): boolean {
    return !component.chemicalId && !component.materialId;
  }

  protected topLevelComponents(): FormulationComponentDto[] {
    if (!this.data) return [];

    const topLevel = this.data.formulationComponents.filter(fc => !fc.parentFormulationComponentId);
    return topLevel.filter(fc => !this.isBlendComponent(fc));
  }

  protected blendForFormulation(): FormulationComponentDto | null {
    if (!this.data) return null;
    return this.data.formulationComponents.find(fc => this.isBlendComponent(fc)) ?? null;
  }

  protected blendChildProportionalWeight(design: DesignIterationDto, blendFcId: number, childFcId: number): number {
    const blendWeight = this.getEffectiveWeight(design, blendFcId);
    const children = this.blendChildren(blendFcId);
    const totalChildrenWeight = children.reduce((sum, c) => sum + this.getEffectiveWeight(design, c.id), 0);
    if (totalChildrenWeight === 0) return 0;
    const childWeight = this.getEffectiveWeight(design, childFcId);
    return (blendWeight * childWeight) / totalChildrenWeight;
  }

  protected topLevelBlendComponents(): FormulationComponentDto[] {
    if (!this.data) return [];

    const topLevel = this.data.formulationComponents.filter(fc => !fc.parentFormulationComponentId);
    return topLevel.filter(fc => this.isBlendComponent(fc));
  }

  protected blendChildren(parentBlendId: number): FormulationComponentDto[] {
    if (!this.data) return [];
    return this.data.formulationComponents.filter(fc => fc.parentFormulationComponentId === parentBlendId);
  }

  protected onBlendChemicalSelectionChange(parentBlendId: number, value: string): void {
    const parsed = Number.parseInt(value, 10);
    const next = { ...this.selectedBlendChemicalIds() };
    next[parentBlendId] = Number.isNaN(parsed) ? null : parsed;
    this.selectedBlendChemicalIds.set(next);
  }

  protected blendSelectedChemicalId(parentBlendId: number): number | null {
    return this.selectedBlendChemicalIds()[parentBlendId] ?? null;
  }

  private computeWeightMaps(): void {
    if (!this.data) {
      this.designWeightMaps.set(new Map());
      return;
    }
    const maps = new Map<number, Map<number, number>>();
    this.data.designs.forEach(design => {
      const map = new Map<number, number>();
      design.materials.forEach(m => {
        if (m.formulationComponentId != null) {
          map.set(m.formulationComponentId, m.weight);
        }
      });
      maps.set(design.id, map);
    });
    this.designWeightMaps.set(maps);
  }

  private initializeSequenceDraft(): void {
    if (!this.data) {
      this.sequenceDraft.set({});
      return;
    }

    const next: Record<number, string> = {};
    this.data.formulationComponents.forEach(fc => {
      next[fc.id] = fc.sequenceNumber === null ? '' : String(fc.sequenceNumber);
    });
    this.sequenceDraft.set(next);
  }

  private initializeSpecificGravityDraft(): void {
    if (!this.data) {
      this.specificGravityDraft.set({});
      return;
    }

    const next: Record<number, string> = {};
    this.data.formulationComponents.forEach(fc => {
      next[fc.id] = String(fc.specificGravity);
    });
    this.specificGravityDraft.set(next);
  }

  private initializeDesignNameDraft(): void {
    if (!this.data) {
      this.designNameDraft.set({});
      return;
    }

    const next: Record<number, string> = {};
    this.data.designs.forEach((design, index) => {
      next[design.id] = this.defaultDesignName(design, index);
    });
    this.designNameDraft.set(next);
  }

  private defaultDesignName(design: DesignIterationDto, index: number): string {
    const explicitName = design.name?.trim();
    if (explicitName) return explicitName;
    return `Iteration ${index + 1}`;
  }

  private parseSequenceNumber(value: string): number | null {
    const trimmed = value.trim();
    if (!trimmed) return null;

    const parsed = Number.parseInt(trimmed, 10);
    if (Number.isNaN(parsed) || parsed <= 0) {
      return null;
    }

    return parsed;
  }

  private loadChemicalsIfNeeded(): void {
    if (!this.data?.isEditable) return;
    if (this.chemicals().length > 0) return;

    this.chemicalsService.getAll().subscribe({
      next: chemicals => this.chemicals.set(chemicals ?? []),
      error: () => {
        this.chemicals.set([]);
        this.actionError.set('Unable to load chemicals.');
      },
    });
  }

  protected isAnyActionSaving(): boolean {
    return this.isSaving() || this.isAddingMaterial() || this.isAddingBlend() || this.isUpdatingSequence() || this.isRemoving() || this.isSettingShippingVolume() || this.isSavingSpecificGravity();
  }

  private getWeightKey(designId: number, componentId: number): string {
    return `${designId}_${componentId}`;
  }

  private initializeWeightDraft(): void {
    const weightDraft: Record<string, string> = {};
    const originalWeights: Record<string, number> = {};

    if (this.data) {
      this.data.designs.forEach(design => {
        design.materials.forEach(m => {
          if (m.formulationComponentId != null) {
            const key = this.getWeightKey(design.id, m.formulationComponentId);
            const weight = m.weight ?? 0;
            weightDraft[key] = weight.toString();
            originalWeights[key] = weight;
          }
        });
      });
    }

    this.weightDraft.set(weightDraft);
    this.originalWeights.set(originalWeights);
  }

  protected weightDraftValue(designId: number, componentId: number): string {
    const key = this.getWeightKey(designId, componentId);
    return this.weightDraft()[key] ?? '0';
  }

  protected dilutionWeightDisplayValue(designId: number, componentId: number): string {
    const value = this.weightDraftValue(designId, componentId);
    const original = this.originalWeights()[this.getWeightKey(designId, componentId)];
    return original !== undefined && value === original.toString() ? original.toFixed(2) : value;
  }

  protected onWeightInput(designId: number, componentId: number, value: string): void {
    if (!this.isDesignEditableById(designId)) return;

    const design = this.data?.designs.find(item => item.id === designId);
    const component = this.data?.formulationComponents.find(item => item.id === componentId);
    if (design?.isShipping && (!component || !this.isShippingMudWeightEditable(design, component))) return;
    if (design && component && this.isDensityChangeComputedWeight(design, component)) {
      return;
    }

    const key = this.getWeightKey(designId, componentId);
    const next = { ...this.weightDraft() };
    next[key] = value;
    this.weightDraft.set(next);
    this.updateLegacyCutbackWarnings();
  }

  protected hasPendingWeightChanges(): boolean {
    if (!this.data || !this.data.isEditable) return false;

    for (const design of this.data.designs) {
      if (!this.canEditDesign(design)) continue;

      if (design.isShipping) {
        if (this.hasPendingShippingMudWeightChange(design)) return true;
        continue;
      }

      const dilutionPercent = this.parsedDilutionPercent(design);
      if (dilutionPercent === null || dilutionPercent !== (design.baseFluidDilutionPercent ?? 0)) return true;

      for (const component of this.data.formulationComponents) {
        const key = this.getWeightKey(design.id, component.id);
        const parsedWeight = this.resolveWeightForSave(design, component.id);
        const originalWeight = this.originalWeights()[key] ?? 0;

        if (parsedWeight === null) {
          return true;
        }

        if (parsedWeight !== originalWeight) {
          return true;
        }
      }
    }

    return false;
  }

  protected onCommitCalculationInput(design: DesignIterationDto): void {
    if (!this.data || !this.canEditDesign(design) || this.isAnyActionSaving()
      || design.testGroups.some(group => group.testInputs.some(test => test.status?.toLowerCase() === 'passed')))
      return;

    const shippingMudEdit = design.isShipping && this.hasPendingShippingMudWeightChange(design);
    if ((this.data.jobRequestTypeId !== DENSITY_CHANGE_JOB_REQUEST_TYPE_ID && !shippingMudEdit)
      || (design.isShipping && !shippingMudEdit)
      || !this.hasPendingWeightChanges()) return;

    this.onSaveWeightChanges();
  }

  protected onSaveWeightChanges(afterSave?: () => void): void {
    if (!this.data || !this.data.isEditable || this.isAnyActionSaving()) return;

    if (this.data.designs.some(design =>
      this.canEditDesign(design) && !design.isShipping && this.parsedDilutionPercent(design) === null
    )) {
      this.actionError.set('Dilution must be a valid percentage greater than or equal to 0 and less than 100.');
      return;
    }

    const cutbackWarnings = this.getLegacyCutbackWarnings();
    if (cutbackWarnings.length > 0) {
      this.actionWarning.set(cutbackWarnings.join(' '));
      if (cutbackWarnings.includes("Weight can't be decreased")) {
        return;
      }
    } else {
      this.actionWarning.set(null);
    }

    const updates: { designId: number; dto: SaveDesignDto }[] = [];
    const changedWeightValues: Record<string, number> = {};
    let hasInvalidWeight = false;

    for (const design of this.data.designs) {
      if (!this.canEditDesign(design)) continue;

      if (design.isShipping) {
        if (!this.hasPendingShippingMudWeightChange(design)) continue;

        const mudComponent = this.data.formulationComponents.find(fc =>
          fc.parentFormulationComponentId == null
          && fc.isDrillingMud
          && this.isShippingMudWeightEditable(design, fc)
        );
        const mudWeight = mudComponent
          ? this.parseWeight(this.weightDraftValue(design.id, mudComponent.id))
          : null;
        if (!mudComponent || mudWeight === null) {
          hasInvalidWeight = true;
          continue;
        }

        const key = this.getWeightKey(design.id, mudComponent.id);
        changedWeightValues[key] = mudWeight;
        updates.push({
          designId: design.id,
          dto: {
            name: design.name,
            designTypeId: design.designTypeId,
            previousDesignId: design.previousDesignId ?? undefined,
            labVolume: design.labVolume ?? undefined,
            materials: [],
            shippingVersion: design.shippingVersion ?? undefined,
            shippingEdits: [{ kind: 'original-mud', formulationComponentId: mudComponent.id, value: mudWeight }],
          },
        });
        continue;
      }

      const dilutionPercent = this.parsedDilutionPercent(design);
      let hasDesignWeightChanges = dilutionPercent !== (design.baseFluidDilutionPercent ?? 0);

      const materials = design.materials.map(material => {
        if (material.formulationComponentId == null) {
          return {
            id: material.id,
            weight: material.weight,
            formulationComponentId: undefined,
          };
        }

        const key = this.getWeightKey(design.id, material.formulationComponentId);
        const parsedWeight = this.resolveWeightForSave(design, material.formulationComponentId);

        if (parsedWeight === null) {
          hasInvalidWeight = true;
          return {
            id: material.id,
            weight: material.weight,
            formulationComponentId: material.formulationComponentId ?? undefined,
          };
        }

        if (parsedWeight !== material.weight) {
          hasDesignWeightChanges = true;
          changedWeightValues[key] = parsedWeight;
        }

        return {
          id: material.id,
          weight: parsedWeight,
          formulationComponentId: material.formulationComponentId ?? undefined,
        };
      });

        const includedComponentIds = new Set(
          materials
            .map(material => material.formulationComponentId)
            .filter((componentId): componentId is number => componentId != null)
        );
        for (const component of this.data.formulationComponents) {
          if (includedComponentIds.has(component.id)) continue;

          const key = this.getWeightKey(design.id, component.id);
          const parsedWeight = this.resolveWeightForSave(design, component.id);
          if (parsedWeight === null) {
            hasInvalidWeight = true;
          } else if (parsedWeight !== 0) {
            hasDesignWeightChanges = true;
            changedWeightValues[key] = parsedWeight;
          }

          materials.push({
            id: undefined,
            weight: parsedWeight ?? 0,
            formulationComponentId: component.id,
          });
        }

      if (hasDesignWeightChanges) {
        updates.push({
          designId: design.id,
          dto: {
            name: design.name,
            designTypeId: design.designTypeId,
            previousDesignId: design.previousDesignId ?? undefined,
            labVolume: design.labVolume ?? undefined,
            baseFluidDilutionPercent: dilutionPercent ?? undefined,
            materials,
          },
        });
      }
    }

    if (hasInvalidWeight) {
      this.actionWarning.set(null);
      this.actionError.set('Weight must be a valid non-negative number.');
      return;
    }

    if (updates.length === 0) {
      this.actionWarning.set(null);
      this.actionError.set(null);
      afterSave?.();
      return;
    }

    this.actionWarning.set(null);
    this.actionError.set(null);
    this.isSaving.set(true);

    const requests = updates.map(update => this.designService.updateDesign(update.designId, update.dto));
    forkJoin(requests).subscribe({
      next: () => {
        this.isSaving.set(false);

        const nextOriginalWeights = { ...this.originalWeights() };
        const nextDraftWeights = { ...this.weightDraft() };
        Object.entries(changedWeightValues).forEach(([key, value]) => {
          nextOriginalWeights[key] = value;
          nextDraftWeights[key] = value.toString();
        });

        this.originalWeights.set(nextOriginalWeights);
        this.weightDraft.set(nextDraftWeights);
        if (afterSave) {
          afterSave();
        } else {
          this.changed.emit();
        }
      },
      error: (error: unknown) => {
        this.isSaving.set(false);
        const validationErrors = (error as { error?: { errors?: Record<string, unknown> } })?.error?.errors;
        const versionError = validationErrors?.['shippingVersion'];
        const versionMessage = Array.isArray(versionError) ? versionError[0] : versionError;
        this.actionError.set(typeof versionMessage === 'string' ? versionMessage : 'Unable to update weights.');
      },
    });
  }

  private createShippingIteration(baseDesignId: number): void {
    const jobRequestId = this.data?.jobRequestId;
    if (jobRequestId == null) return;

    this.isSaving.set(true);
    this.designService
      .createShippingIteration(jobRequestId, { baseDesignId })
      .subscribe({
        next: () => {
          this.isSaving.set(false);
          this.changed.emit();
        },
        error: () => {
          this.isSaving.set(false);
          this.actionError.set('Unable to create shipping iteration.');
        },
      });
  }

  private getBlendWeightValidationErrors(): string[] {
    if (!this.data) return [];

    const blendComponents = this.topLevelBlendComponents();
    if (blendComponents.length === 0) return [];

    const errors: string[] = [];
    const epsilon = 0.0001;

    this.data.designs.forEach((design, index) => {
      blendComponents.forEach(blend => {
        const blendWeight = this.parseWeight(this.weightDraftValue(design.id, blend.id));
        const children = this.blendChildren(blend.id);

        if (blendWeight === null || children.length === 0) {
          return;
        }

        let childrenWeightSum = 0;
        for (const child of children) {
          const childWeight = this.parseWeight(this.weightDraftValue(design.id, child.id));
          if (childWeight === null) {
            return;
          }
          childrenWeightSum += childWeight;
        }

        if (Math.abs(childrenWeightSum - blendWeight) > epsilon) {
          const designName = this.defaultDesignName(design, index);
          const blendName = blend.name ?? blend.materialName ?? blend.chemicalName ?? `Blend ${blend.id}`;
          errors.push(
            `${designName}: ${blendName} total child weight (${childrenWeightSum.toFixed(2)} g) must equal blend weight (${blendWeight.toFixed(2)} g).`
          );
        }
      });
    });

    return errors;
  }

  private parseWeight(value: string): number | null {
    const trimmed = value.trim();
    if (!trimmed) return 0;

    const parsed = Number.parseFloat(trimmed);
    if (Number.isNaN(parsed) || parsed < 0) {
      return null;
    }

    return parsed;
  }

  protected filteredChemicals(): ChemicalListItemDto[] {
    const term = this.chemicalSearchTerm().toLowerCase();
    if (!term) return this.chemicals();
    
    return this.chemicals().filter(c => 
      c.name.toLowerCase().includes(term)
    );
  }

  protected filteredBlendChemicals(parentBlendId: number): ChemicalListItemDto[] {
    const term = (this.blendChemicalSearchTerms()[parentBlendId] ?? '').toLowerCase();
    if (!term) return this.chemicals();
    
    return this.chemicals().filter(c => 
      c.name.toLowerCase().includes(term)
    );
  }

  protected onChemicalSearchInput(value: string): void {
    this.chemicalSearchTerm.set(value);
  }

  protected onBlendChemicalSearchInput(parentBlendId: number, value: string): void {
    const next = { ...this.blendChemicalSearchTerms() };
    next[parentBlendId] = value;
    this.blendChemicalSearchTerms.set(next);
  }

  protected showBlendChemicalSearchFor(parentBlendId: number): void {
    const next = { ...this.showBlendChemicalSearch() };
    next[parentBlendId] = true;
    this.showBlendChemicalSearch.set(next);
  }

  protected onSelectChemical(chemicalId: number): void {
    this.selectedChemicalId.set(chemicalId);
    this.chemicalSearchTerm.set('');
  }

  protected onSelectAndAddChemical(chemicalId: number): void {
    this.selectedChemicalId.set(chemicalId);
    this.chemicalSearchTerm.set('');
    this.showChemicalSearch.set(false);
    this.onAddMaterial();
  }

  protected onSelectBlendChemical(parentBlendId: number, chemicalId: number): void {
    const next = { ...this.selectedBlendChemicalIds() };
    next[parentBlendId] = chemicalId;
    this.selectedBlendChemicalIds.set(next);
    
    const searchTerms = { ...this.blendChemicalSearchTerms() };
    searchTerms[parentBlendId] = '';
    this.blendChemicalSearchTerms.set(searchTerms);
  }

  protected onSelectAndAddBlendChemical(parentBlendId: number, chemicalId: number): void {
    const next = { ...this.selectedBlendChemicalIds() };
    next[parentBlendId] = chemicalId;
    this.selectedBlendChemicalIds.set(next);
    
    const searchTerms = { ...this.blendChemicalSearchTerms() };
    searchTerms[parentBlendId] = '';
    this.blendChemicalSearchTerms.set(searchTerms);

    const showMap = { ...this.showBlendChemicalSearch() };
    showMap[parentBlendId] = false;
    this.showBlendChemicalSearch.set(showMap);
    
    this.onAddMaterial(parentBlendId);
  }

  protected getSelectedChemicalName(): string {
    const id = this.selectedChemicalId();
    if (!id) return '';
    return this.chemicals().find(c => c.id === id)?.name ?? '';
  }

  protected getSelectedBlendChemicalName(parentBlendId: number): string {
    const id = this.selectedBlendChemicalIds()[parentBlendId];
    if (!id) return '';
    return this.chemicals().find(c => c.id === id)?.name ?? '';
  }

  private static readonly WATER_DENSITY_PPG = 8.345;
  private static readonly MILLILITERS_PER_GALLON = 3785.41;

  protected dilutionPercentValue(design: DesignIterationDto): string {
    return this.dilutionPercentDraft()[design.id] ?? String(design.baseFluidDilutionPercent ?? 0);
  }

  private parsedDilutionPercent(design: DesignIterationDto): number | null {
    const draft = this.dilutionPercentDraft()[design.id];
    if (draft === undefined) return design.baseFluidDilutionPercent ?? 0;
    const value = draft.trim();
    const percent = Number(value);
    return value !== '' && Number.isFinite(percent) && percent >= 0 && percent < 100 ? percent : null;
  }

  protected onDilutionPercentInput(design: DesignIterationDto, value: string): void {
    if (!this.canEditDesign(design) || design.isShipping || this.isAnyActionSaving()
      || this.data?.densityChangeClassification !== 'weighup') return;

    this.dilutionPercentDraft.update(draft => ({ ...draft, [design.id]: value }));
    this.actionError.set(this.parsedDilutionPercent(design) === null
      ? 'Dilution must be a valid percentage greater than or equal to 0 and less than 100.'
      : null);
    this.updateLegacyCutbackWarnings();
  }

  protected dilutionFluidDensityLbPerBbl(design: DesignIterationDto): number | null {
    const blend = this.blendForFormulation();
    if (!blend) return null;

    const children = this.blendChildren(blend.id);
    if (children.length === 0) return null;

    let totalWeight = 0;
    let totalVolume = 0;

    for (const child of children) {
      const weight = this.getEffectiveWeight(design, child.id);
      const sg = child.specificGravity;
      if (sg > 0) {
        totalWeight += weight;
        totalVolume += weight / sg;
      }
    }

    if (totalVolume === 0) return null;
    return (totalWeight / totalVolume) * FormulationIterationsComponent.WATER_DENSITY_PPG;
  }

  protected formulationDensityLbPerBbl(design: DesignIterationDto): number | null {
    const sg = this.formulationSpecificGravity(design);
    return sg === null ? null : sg * FormulationIterationsComponent.WATER_DENSITY_PPG;
  }

  /** Current specific gravity (g/ml) = total weight / total volume of the primary materials. */
  protected formulationSpecificGravity(design: DesignIterationDto): number | null {
    let totalWeight = 0;
    let totalVolume = 0;

    for (const fc of this.topLevelComponents()) {
      const weight = this.getEffectiveWeight(design, fc.id);
      const sg = fc.specificGravity;
      if (sg > 0) {
        totalWeight += weight;
        totalVolume += weight / sg;
      }
    }

    const blend = this.blendForFormulation();
    if (blend) {
      const blendWeight = this.getEffectiveWeight(design, blend.id);
      const children = this.blendChildren(blend.id);

      let childTotalWeight = 0;
      let childTotalVolume = 0;
      for (const child of children) {
        const childW = this.getEffectiveWeight(design, child.id);
        const childSG = child.specificGravity;
        if (childSG > 0) {
          childTotalWeight += childW;
          childTotalVolume += childW / childSG;
        }
      }

      const blendSG = childTotalVolume > 0 ? childTotalWeight / childTotalVolume : 0;
      if (blendSG > 0) {
        totalWeight += blendWeight;
        totalVolume += blendWeight / blendSG;
      }
    }

    if (totalVolume === 0) return null;
    return totalWeight / totalVolume;
  }

  protected baselineDensityLabel(): string | null {
    const data = this.data;
    if (!data || data.jobRequestTypeId !== DENSITY_CHANGE_JOB_REQUEST_TYPE_ID) return null;

    if (data.baselineDensityPpg == null || data.baselineDensitySource === 'None') {
      return 'Baseline density: not available (no Pycnometer or Density test result)';
    }

    const source = data.baselineDensitySource === 'DensityTest' ? 'Density test' : 'Pycnometer';
    return `Baseline density ${data.baselineDensityPpg.toFixed(2)} ppg (${source})`;
  }

  // ─── Shipping-volume helpers ────────────────────────────────────────────────

  /**
   * Mirrors DensityCalculator.GetShippingDilutionFluidFactor.
   * Returns the display multiplier for dilution-fluid child weights in a
   * shipping column: factor = (blendVolume × ((volPct + 0.05) / volPct)) / dilutionFluidVolume
   * where volPct = blendVolume / designTotalVolume.
   * Always returns 1 for non-shipping designs.
   */
  protected shippingDilutionFactor(design: DesignIterationDto): number {
    if (!design.isShipping) return 1;

    const blend = this.blendForFormulation();
    if (!blend) return 1;

    const blendSG = this.blendEffectiveSG(design);
    if (blendSG === 0) return 1;

    const effectiveBlendWeight = this.getEffectiveWeight(design, blend.id);
    const blendVolume = effectiveBlendWeight / blendSG;

    const totalVolume = this.designTotalVolumeMl(design);
    if (totalVolume === 0 || blendVolume === 0) return 1;

    const volPct = blendVolume / totalVolume;
    if (volPct === 0) return 1;

    const children = this.blendChildren(blend.id);
    let dilutionFluidVolume = 0;
    for (const child of children) {
      const w = this.getEffectiveWeight(design, child.id);
      if (child.specificGravity > 0) dilutionFluidVolume += w / child.specificGravity;
    }

    if (dilutionFluidVolume === 0) return 1;

    const ratio = (volPct + 0.05) / volPct;
    return (blendVolume * ratio) / dilutionFluidVolume;
  }

  /**
   * Displayed weight of a blend child in a shipping column = stored weight × dilutionFactor.
   * For non-shipping columns returns the plain stored weight.
   */
  protected displayChildWeight(design: DesignIterationDto, childFcId: number): number {
    const stored = this.getEffectiveWeight(design, childFcId);
    return design.isShipping ? stored * this.shippingDilutionFactor(design) : stored;
  }

  /** Total volume (ml) for PRIMARY components — mirrors Design.TotalVolume in the legacy model. */
  private designTotalVolumeMl(design: DesignIterationDto): number {
    let total = 0;
    if (!this.data) return 0;
    for (const fc of this.data.formulationComponents.filter(fc => !fc.parentFormulationComponentId)) {
      const sg = this.getComponentEffectiveSG(design, fc);
      if (sg > 0) {
        const w = this.getEffectiveWeight(design, fc.id);
        total += w / sg;
      }
    }
    return total;
  }

  /** Effective SG of a blend in the given design (weight-averaged from children). */
  private blendEffectiveSG(design: DesignIterationDto): number {
    const blend = this.blendForFormulation();
    if (!blend) return 0;
    return this.getComponentEffectiveSG(design, blend);
  }

  /** Returns the effective SG for any component (blend → derived; normal → fc.specificGravity). */
  private getComponentEffectiveSG(design: DesignIterationDto, fc: FormulationComponentDto): number {
    const isBlend = !fc.chemicalId && !fc.materialId;
    if (!isBlend) return fc.specificGravity;

    const children = this.blendChildren(fc.id);
    if (children.length === 0) return 0;

    let totalW = 0;
    let totalV = 0;
    for (const child of children) {
      const w = this.parseWeight(this.weightDraftValue(design.id, child.id)) ?? 0;
      if (child.specificGravity > 0) {
        totalW += w;
        totalV += w / child.specificGravity;
      }
    }
    return totalV === 0 ? 0 : totalW / totalV;
  }

  // ─── Volume, gal draft management ───────────────────────────────────────────

  private initializeVolumeGalDraft(): void {
    if (!this.data) {
      this.volumeGalDraft.set({});
      this.volumeBarrelDraft.set({});
      return;
    }
    const nextGallons: Record<number, string> = {};
    const nextBarrels: Record<number, string> = {};
    for (const design of this.data.designs) {
      if (design.isVolumeEditable && design.totalVolumeGallons !== null) {
        nextGallons[design.id] = design.totalVolumeGallons.toString();
        nextBarrels[design.id] = this.gallonsToBarrels(design.totalVolumeGallons).toFixed(3);
      }
    }
    this.volumeGalDraft.set(nextGallons);
    this.volumeBarrelDraft.set(nextBarrels);
  }

  protected volumeGalValue(design: DesignIterationDto): string {
    if (design.isVolumeEditable) {
      return this.volumeGalDraft()[design.id] ?? (design.totalVolumeGallons?.toString() ?? '');
    }
    return design.totalVolumeGallons !== null ? design.totalVolumeGallons.toFixed(3) : '';
  }

  protected volumeBarrelValue(design: DesignIterationDto): string {
    if (design.isVolumeEditable) {
      return this.volumeBarrelDraft()[design.id] ?? (design.totalVolumeGallons !== null ? this.gallonsToBarrels(design.totalVolumeGallons).toFixed(3) : '');
    }
    return design.totalVolumeGallons !== null ? this.gallonsToBarrels(design.totalVolumeGallons).toFixed(3) : '';
  }

  protected onVolumeGalInput(design: DesignIterationDto, value: string): void {
    if (!design.isVolumeEditable) return;
    this.setVolumeDraft(design.id, value, 'gallons');
  }

  protected onVolumeBarrelInput(design: DesignIterationDto, value: string): void {
    if (!design.isVolumeEditable) return;
    this.setVolumeDraft(design.id, value, 'barrels');
  }

  protected onVolumeGalEnter(event: Event): void {
    event.preventDefault();
    (event.target as HTMLInputElement | null)?.blur();
  }

  protected onCommitVolumeGal(design: DesignIterationDto): void {
    this.commitShippingVolume(design, 'gallons');
  }

  protected onCommitVolumeBarrel(design: DesignIterationDto): void {
    this.commitShippingVolume(design, 'barrels');
  }

  protected isShippingMudWeightEditable(design: DesignIterationDto, component: FormulationComponentDto): boolean {
    if (!this.data || !this.canEditDesign(design) || !design.isShipping || !design.isVolumeEditable) return false;

    const mudComponents = this.data.formulationComponents.filter(fc =>
      fc.parentFormulationComponentId == null && fc.isDrillingMud
    );
    return mudComponents.length === 1 && mudComponents[0].id === component.id;
  }

  private hasPendingShippingMudWeightChange(design: DesignIterationDto): boolean {
    if (!design.isShipping || !design.isVolumeEditable) return false;

    const mudComponent = this.data?.formulationComponents.find(fc =>
      fc.parentFormulationComponentId == null
      && fc.isDrillingMud
      && this.isShippingMudWeightEditable(design, fc)
    );
    if (!mudComponent) return false;

    const key = this.getWeightKey(design.id, mudComponent.id);
    const parsedWeight = this.parseWeight(this.weightDraftValue(design.id, mudComponent.id));
    return parsedWeight === null || parsedWeight !== (this.originalWeights()[key] ?? 0);
  }

  private commitShippingVolume(design: DesignIterationDto, unit: 'gallons' | 'barrels'): void {
    if (!this.data || !design.isVolumeEditable || this.isAnyActionSaving()) return;

    const raw = unit === 'gallons'
      ? this.volumeGalDraft()[design.id] ?? ''
      : this.volumeBarrelDraft()[design.id] ?? '';
    const parsed = Number.parseFloat(raw.trim());
    const gallons = unit === 'gallons' ? parsed : this.barrelsToGallons(parsed);

    if (Number.isNaN(parsed) || parsed <= 0) {
      this.actionError.set('Volume must be a positive number.');
      this.resetVolumeDrafts(design);
      return;
    }

    const current = design.totalVolumeGallons;
    if (current !== null && Math.abs(gallons - current) < 0.0001) return;

    const dto: SetShippingVolumeDto = { totalVolumeGallons: gallons };
    this.actionError.set(null);
    this.isSettingShippingVolume.set(true);
    this.designService.setShippingVolume(design.id, dto).subscribe({
      next: () => {
        this.isSettingShippingVolume.set(false);
        this.changed.emit();
      },
      error: () => {
        this.isSettingShippingVolume.set(false);
        this.actionError.set('Unable to update shipping volume.');
        this.resetVolumeDrafts(design);
      },
    });
  }

  private setVolumeDraft(designId: number, rawValue: string, unit: 'gallons' | 'barrels'): void {
    const nextGallons = { ...this.volumeGalDraft() };
    const nextBarrels = { ...this.volumeBarrelDraft() };

    if (unit === 'gallons') {
      nextGallons[designId] = rawValue;
    } else {
      nextBarrels[designId] = rawValue;
    }

    const parsed = Number.parseFloat(rawValue.trim());
    if (!Number.isNaN(parsed) && parsed > 0) {
      if (unit === 'gallons') {
        nextBarrels[designId] = this.gallonsToBarrels(parsed).toFixed(3);
      } else {
        nextGallons[designId] = this.barrelsToGallons(parsed).toFixed(3);
      }
    }

    this.volumeGalDraft.set(nextGallons);
    this.volumeBarrelDraft.set(nextBarrels);
  }

  private resetVolumeDrafts(design: DesignIterationDto): void {
    const nextGallons = { ...this.volumeGalDraft() };
    const nextBarrels = { ...this.volumeBarrelDraft() };

    if (design.totalVolumeGallons !== null) {
      nextGallons[design.id] = design.totalVolumeGallons.toString();
      nextBarrels[design.id] = this.gallonsToBarrels(design.totalVolumeGallons).toFixed(3);
    } else {
      delete nextGallons[design.id];
      delete nextBarrels[design.id];
    }

    this.volumeGalDraft.set(nextGallons);
    this.volumeBarrelDraft.set(nextBarrels);
  }

  private gallonsToBarrels(gallons: number): number {
    return gallons * BARRELS_PER_GALLON;
  }

  private barrelsToGallons(barrels: number): number {
    return barrels / BARRELS_PER_GALLON;
  }

  // ─── Inline test results ───────────────────────────────────────────────────

  protected hasInlineTestResults(): boolean {
    const hasDesignOutputs = !!this.data?.designs.some(d =>
      d.testGroups.some(tg => tg.testInputs.some(ti => ti.testOutputs.length > 0))
    );
    const hasMudCheckOutputs = !!this.data?.mudCheckDesign?.testGroups.some(tg =>
      tg.testInputs.some(ti => ti.testOutputs.length > 0)
    );
    return hasDesignOutputs || hasMudCheckOutputs;
  }

  // ─── Weight / volume tooltips ──────────────────────────────────────────────

  protected designTotalWeightGrams(design: DesignIterationDto): number {
    let total = 0;
    for (const fc of this.topLevelComponents()) {
      total += this.getEffectiveWeight(design, fc.id);
    }

    const blend = this.blendForFormulation();
    if (blend) {
      total += this.getEffectiveWeight(design, blend.id);
    }

    return total;
  }

  protected isDensityChangeComputedWeight(design: DesignIterationDto, component: FormulationComponentDto): boolean {
    return this.getAutoCalculatedWeights(design)?.has(component.id) ?? false;
  }

  protected isSpecificGravityReadOnly(component: FormulationComponentDto): boolean {
    if (!this.data
      || this.data.jobRequestTypeId !== DENSITY_CHANGE_JOB_REQUEST_TYPE_ID
      || this.data.densityChangeClassification !== 'cutback') {
      return false;
    }

    const mudComponentId = this.resolveCutbackMudComponentId();
    return mudComponentId != null && component.id === mudComponentId;
  }

  protected displayWeightValue(design: DesignIterationDto, componentId: number): number {
    return this.getEffectiveWeight(design, componentId);
  }

  private resolveWeightForSave(design: DesignIterationDto, componentId: number): number | null {
    const autoWeight = this.getAutoCalculatedWeights(design)?.get(componentId);
    if (autoWeight != null) {
      return autoWeight;
    }

    return this.parseWeight(this.weightDraftValue(design.id, componentId));
  }

  private getEffectiveWeight(design: DesignIterationDto, componentId: number): number {
    const autoWeight = this.getAutoCalculatedWeights(design)?.get(componentId);
    if (autoWeight != null) {
      return autoWeight;
    }

    return this.parseWeight(this.weightDraftValue(design.id, componentId)) ?? 0;
  }

  private getAutoCalculatedWeights(design: DesignIterationDto): Map<number, number> | null {
    const data = this.data;
    if (!data
      || data.jobRequestTypeId !== DENSITY_CHANGE_JOB_REQUEST_TYPE_ID
      || (data.densityChangeClassification !== 'cutback' && data.densityChangeClassification !== 'weighup')
      || design.isShipping
      || data.targetDensity == null
      || data.targetDensity <= 0) {
      return null;
    }

    if (data.densityChangeClassification === 'weighup') {
      return this.getWeighUpAutoCalculatedWeights(design);
    }

    const topLevel = this.topLevelComponents()
      .slice()
      .sort((left, right) => (left.sequenceNumber ?? Number.MAX_SAFE_INTEGER) - (right.sequenceNumber ?? Number.MAX_SAFE_INTEGER) || left.id - right.id);

    const adjustmentComponent = topLevel.find(component => component.isBaseFluid) ?? this.blendForFormulation();
    if (!adjustmentComponent) {
      return null;
    }

    const mudComponent = topLevel
      .filter(component => component.id !== adjustmentComponent.id && !component.isBaseFluid)
      .sort((left, right) => (left.sequenceNumber ?? Number.MAX_SAFE_INTEGER) - (right.sequenceNumber ?? Number.MAX_SAFE_INTEGER) || left.id - right.id)[0];

    if (!mudComponent || mudComponent.specificGravity <= 0) {
      return null;
    }

    const persistedWeights = this.weightMap(design);
    return new Map<number, number>([
      [mudComponent.id, persistedWeights.get(mudComponent.id) ?? 0],
      [adjustmentComponent.id, persistedWeights.get(adjustmentComponent.id) ?? 0],
    ]);
  }

  private getWeighUpAutoCalculatedWeights(design: DesignIterationDto): Map<number, number> | null {
    const data = this.data;
    if (!data || data.targetDensity == null) return null;

    const topLevel = this.topLevelComponents()
      .slice()
      .sort((left, right) => (left.sequenceNumber ?? Number.MAX_SAFE_INTEGER) - (right.sequenceNumber ?? Number.MAX_SAFE_INTEGER) || left.id - right.id);
    const mud = topLevel.find(component => component.isDrillingMud);
    const weightingAgent = topLevel.find(component => component.isWeightingAgent);
    if (!mud || mud.specificGravity <= 0 || !weightingAgent) return null;

    const baseFluid = topLevel.find(component => component.isBaseFluid || this.isBlendComponent(component));
    const targetSpecificGravity = Math.round((data.targetDensity / FormulationIterationsComponent.WATER_DENSITY_PPG) * 10000) / 10000;
    const weightingAgentSg = this.getComponentEffectiveSG(design, weightingAgent);
    if (weightingAgentSg <= 0) return null;

    const previousDesign = design.previousDesignId == null
      ? null
      : data.designs.find(item => item.id === design.previousDesignId) ?? null;
    const previousWeight = (componentId: number): number =>
      previousDesign?.materials.find(material => material.formulationComponentId === componentId)?.weight ?? 0;
    const clampWeight = (componentId: number, weight: number): number =>
      Math.max(this.roundWeight(weight), previousWeight(componentId));

    if (previousDesign) {
      const calculated = new Map<number, number>();
      const dilution = (this.parsedDilutionPercent(design) ?? design.baseFluidDilutionPercent ?? 0) / 100;

      if (baseFluid) {
        const mudVolume = (this.parseWeight(this.weightDraftValue(design.id, mud.id)) ?? 0) / mud.specificGravity;
        const baseFluidSg = this.getComponentEffectiveSG(design, baseFluid);
        if (baseFluidSg > 0) {
          const baseFluidWeight = dilution > 0 && dilution < 1
            ? (dilution / (1 - dilution)) * mudVolume * baseFluidSg
            : 0;
          calculated.set(baseFluid.id, clampWeight(baseFluid.id, baseFluidWeight));
        }
      }

      let otherWeight = 0;
      let otherVolume = 0;
      for (const component of topLevel.filter(item => item.id !== weightingAgent.id)) {
        const weight = calculated.get(component.id) ?? (this.parseWeight(this.weightDraftValue(design.id, component.id)) ?? 0);
        const sg = this.getComponentEffectiveSG(design, component);
        otherWeight += weight;
        if (sg > 0) otherVolume += weight / sg;
      }

      if (targetSpecificGravity === weightingAgentSg) return null;
      calculated.set(
        weightingAgent.id,
        clampWeight(
          weightingAgent.id,
          ((otherWeight - targetSpecificGravity * otherVolume) * weightingAgentSg)
            / (targetSpecificGravity - weightingAgentSg)
        )
      );
      return calculated;
    }

    const targetVolume = design.labVolume ?? DEFAULT_LAB_VOLUME_ML;
    const dilution = baseFluid ? (this.parsedDilutionPercent(design) ?? design.baseFluidDilutionPercent ?? 0) / 100 : 0;
    const baseFluidSg = baseFluid ? this.getComponentEffectiveSG(design, baseFluid) : 0;
    let additiveWeight = 0;
    let additiveVolume = 0;

    for (const component of topLevel.filter(item =>
      item.id !== mud.id && item.id !== weightingAgent.id && item.id !== baseFluid?.id && !this.isBlendComponent(item)
    )) {
      const weight = this.parseWeight(this.weightDraftValue(design.id, component.id)) ?? 0;
      const sg = this.getComponentEffectiveSG(design, component);
      additiveWeight += weight;
      if (sg > 0) additiveVolume += weight / sg;
    }

    const denominator = mud.specificGravity * (1 - dilution) + baseFluidSg * dilution - weightingAgentSg;
    if (denominator === 0) return null;

    const mudAndBaseFluidVolume = (
      targetSpecificGravity * targetVolume
      - additiveWeight
      - weightingAgentSg * (targetVolume - additiveVolume)
    ) / denominator;
    const mudWeight = mudAndBaseFluidVolume * (1 - dilution) * mud.specificGravity;
    const weightingAgentWeight = (targetVolume - additiveVolume - mudAndBaseFluidVolume) * weightingAgentSg;
    const calculated = new Map<number, number>([
      [mud.id, clampWeight(mud.id, mudWeight)],
      [weightingAgent.id, clampWeight(weightingAgent.id, weightingAgentWeight)],
    ]);

    if (baseFluid && baseFluidSg > 0) {
      calculated.set(
        baseFluid.id,
        clampWeight(baseFluid.id, mudAndBaseFluidVolume * dilution * baseFluidSg)
      );
    }

    return calculated;
  }

  private resolveCutbackMudComponentId(): number | null {
    const data = this.data;
    if (!data
      || data.jobRequestTypeId !== DENSITY_CHANGE_JOB_REQUEST_TYPE_ID
      || data.densityChangeClassification !== 'cutback') {
      return null;
    }

    const topLevel = this.topLevelComponents()
      .slice()
      .sort((left, right) => (left.sequenceNumber ?? Number.MAX_SAFE_INTEGER) - (right.sequenceNumber ?? Number.MAX_SAFE_INTEGER) || left.id - right.id);

    const adjustmentComponent = topLevel.find(component => component.isBaseFluid) ?? this.blendForFormulation();
    const mudComponent = topLevel
      .filter(component => component.id !== adjustmentComponent?.id && !component.isBaseFluid)
      .sort((left, right) => (left.sequenceNumber ?? Number.MAX_SAFE_INTEGER) - (right.sequenceNumber ?? Number.MAX_SAFE_INTEGER) || left.id - right.id)[0];

    return mudComponent?.id ?? null;
  }

  private roundWeight(value: number): number {
    return Math.round(value * 100) / 100;
  }

  protected designWeightTooltip(design: DesignIterationDto): string {
    const wG = this.designTotalWeightGrams(design);
    const vMl = this.designTotalVolumeMl(design);
    const parts: string[] = [];
    if (wG > 0) parts.push(`${wG.toFixed(1)} g`);
    if (vMl > 0) parts.push(`${vMl.toFixed(1)} ml`);
    if (wG > 0 && vMl > 0) parts.push(`${(wG / vMl).toFixed(3)} SG`);
    return parts.length ? `Total: ${parts.join(', ')}` : '';
  }

  private updateLegacyCutbackWarnings(): void {
    const warnings = this.getLegacyCutbackWarnings();
    this.actionWarning.set(warnings.length > 0 ? warnings.join(' ') : null);
  }

  private getLegacyCutbackWarnings(): string[] {
    const data = this.data;
    if (!data || data.jobRequestTypeId !== DENSITY_CHANGE_JOB_REQUEST_TYPE_ID) {
      return [];
    }

    const warnings: string[] = [];

    if (data.designs.length === 1) {
      if (data.densityChangeClassification === 'cutback') {
        const hasBaseFluidOrBlend = data.formulationComponents.some(fc => fc.isBaseFluid || this.isBlendComponent(fc));
        if (!hasBaseFluidOrBlend) {
          warnings.push('Base Fluid is required for cutbacks');
        }
      }

      if (data.densityChangeClassification === 'weighup') {
        const hasWeightingAgent = data.formulationComponents.some(fc => fc.isWeightingAgent);
        if (!hasWeightingAgent) {
          warnings.push('Weighing Agent is required for weigh ups');
        }
      }
    }

    const hasWeightDecrease = data.designs
      .filter(d => !d.isShipping && d.previousDesignId != null)
      .some(design => {
        const previous = data.designs.find(d => d.id === design.previousDesignId);
        if (!previous) return false;

        const previousByComponentId = new Map<number, number>();
        previous.materials.forEach(pm => {
          if (pm.formulationComponentId != null) {
            previousByComponentId.set(pm.formulationComponentId, pm.weight ?? 0);
          }
        });

        return design.materials.some(material => {
          if (material.formulationComponentId == null) return false;
          const previousWeight = previousByComponentId.get(material.formulationComponentId);
          if (previousWeight == null) return false;

          const currentWeight = this.parseWeight(this.weightDraftValue(design.id, material.formulationComponentId));
          if (currentWeight == null) return false;

          return currentWeight < previousWeight - 0.1;
        });
      });

    if (hasWeightDecrease) {
      warnings.push("Weight can't be decreased");
    }

    return warnings;
  }

  /** Get the component type label for display (e.g., [Base Oil], [Additive], [Composite]) */
  protected getComponentTypeLabel(fc: FormulationComponentDto): string {
    if (!fc.materialSubTypeAlias) {
      return 'Composite';
    }
    return fc.materialSubTypeAlias;
  }
}


